import { TranscriptionStatus } from '../types';

export interface TranscriptionState {
  transcriptionStatus: TranscriptionStatus;
  transcriptionLoading: boolean;
  transcriptionError: string | null;
}

export interface TranscriptionActions {
  connectTranscription: (deviceId: string) => void;
  disconnectTranscription: (deviceId: string) => void;
  clearTranscriptionHistory: () => void;
}

export const createTranscriptionModule = (set: any, get: any) => ({
  transcriptionStatus: {
    isActive: false,
    connectedDevices: [],
    lastTranscription: null,
    transcriptionHistory: [],
    liveTranscriptions: {},
    audioAlertVisible: false,
  },
  transcriptionLoading: false,
  transcriptionError: null,

  connectTranscription: (deviceId: string) => {
    const { sdk } = get();
    if (!sdk) return;

    set({ transcriptionLoading: true, transcriptionError: null });

    try {
      sdk.transcription.connect(deviceId, {
        onTranscription: (data: any) => {
          console.log('[Store] onTranscription received', data);
          const serverTimestamp = data.timestamp || data.Timestamp || data.ts;
          const timestamp = serverTimestamp ? new Date(serverTimestamp) : new Date();

          const newMessage = {
            id: `${deviceId}-${data.index || data.Index || Date.now()}`,
            deviceId,
            text: data.text || data.Text || '',
            timestamp,
          };

          set((state: any) => {
            const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
            delete liveTranscriptions[deviceId];

            return {
              transcriptionStatus: {
                ...state.transcriptionStatus,
                isActive: true,
                audioAlertVisible: false, // Reset alert on valid speech
                lastTranscription: {
                  deviceId,
                  text: data.text || data.Text || '',
                  timestamp,
                },
                liveTranscriptions,
                transcriptionHistory: [...state.transcriptionStatus.transcriptionHistory, newMessage],
              },
            };
          });
        },
        onPartial: (data: any) => {
          console.log('[Store] onPartial received', data);
          const serverTimestamp = data.timestamp || data.Timestamp || data.ts;
          const timestamp = serverTimestamp ? new Date(serverTimestamp) : new Date();
          const text = data.text || data.Text || '';

          set((state: any) => ({
            transcriptionStatus: {
              ...state.transcriptionStatus,
              isActive: true,
              audioAlertVisible: false,
              lastTranscription: {
                deviceId,
                text,
                timestamp,
              },
              liveTranscriptions: {
                ...(state.transcriptionStatus.liveTranscriptions || {}),
                [deviceId]: {
                  deviceId,
                  text,
                  timestamp,
                },
              },
            },
          }));
        },
        onPartialEnd: () => {
          set((state: any) => {
            const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
            delete liveTranscriptions[deviceId];
            return {
              transcriptionStatus: {
                ...state.transcriptionStatus,
                liveTranscriptions,
              },
            };
          });
        },
        onAlert: (data: any) => {
          console.log('[Store] onAlert received', data);
          if (data && (data.error || data.Error) === 'NO_AUDIO_DETECTED') {
            console.log('[Store] Setting audioAlertVisible = true. Current status:', get().transcriptionStatus);
            set((state: any) => ({
              transcriptionStatus: {
                ...state.transcriptionStatus,
                audioAlertVisible: true,
              },
            }));
            // Verify immediate update (async)
            setTimeout(() => console.log('[Store] Post-update status:', get().transcriptionStatus), 100);
          }
        },
        onOpen: () => {
          set((state: any) => ({
            transcriptionStatus: {
              ...state.transcriptionStatus,
              isActive: true,
              connectedDevices: [...new Set([...state.transcriptionStatus.connectedDevices, deviceId])],
            },
            transcriptionLoading: false,
            transcriptionError: null,
          }));
        },
        onError: (_error: any) => {
          set({
            transcriptionLoading: false,
            transcriptionError: `Connection error for device ${deviceId}`,
          });
        },
        onClose: () => {
          set((state: any) => {
            const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
            delete liveTranscriptions[deviceId];

            return {
              transcriptionStatus: {
                ...state.transcriptionStatus,
                liveTranscriptions,
                connectedDevices: state.transcriptionStatus.connectedDevices.filter((id: string) => id !== deviceId),
                isActive: state.transcriptionStatus.connectedDevices.length > 1,
              },
            };
          });
        },
      });
    } catch (error) {
      set({
        transcriptionLoading: false,
        transcriptionError: error instanceof Error ? error.message : 'Failed to connect transcription',
      });
    }
  },

  disconnectTranscription: (deviceId: string) => {
    set((state: any) => {
      const liveTranscriptions = { ...(state.transcriptionStatus.liveTranscriptions || {}) };
      delete liveTranscriptions[deviceId];

      return {
        transcriptionStatus: {
          ...state.transcriptionStatus,
          liveTranscriptions,
          connectedDevices: state.transcriptionStatus.connectedDevices.filter((id: string) => id !== deviceId),
          isActive: state.transcriptionStatus.connectedDevices.length > 1,
        },
      };
    });
  },

  clearTranscriptionHistory: () => {
    set((state: any) => ({
      transcriptionStatus: {
        ...state.transcriptionStatus,
        transcriptionHistory: [],
        liveTranscriptions: {},
      },
    }));
  },
});
